import React, { useCallback, useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { httpsCallable } from 'firebase/functions';
import { AlertCircle, Loader2, Lock, X } from 'lucide-react';
import { functions } from '../../config/firebase';
import { Button } from '../ui/button';
import { getStripe, STRIPE_APPEARANCE } from '../../lib/stripeClient';

interface AddPaymentMethodSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after Stripe confirms the card is attached to the customer. */
  onAdded: () => void | Promise<void>;
}

interface SetupIntentResponse {
  clientSecret: string;
  publishableKey: string;
}

function SetupForm({
  onAdded,
  onClose,
}: {
  onAdded: () => void | Promise<void>;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    // 'if_required' keeps the shopper in the sheet for the common case and
    // only leaves the page when the bank actually demands 3DS.
    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/settings/payment` },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'We could not save that card.');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      await onAdded();
      onClose();
      return;
    }

    setError(`Card setup did not complete (${setupIntent?.status ?? 'unknown'}).`);
    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => setReady(true)}
      />

      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5">
          <AlertCircle className="h-4 w-4 text-error mt-0.5 flex-shrink-0" />
          <p className="text-xs text-error">{error}</p>
        </div>
      )}

      <Button type="submit" size="lg" fullWidth loading={submitting} disabled={!ready || !stripe}>
        {!submitting && <Lock className="h-4 w-4 mr-2" />}
        {submitting ? 'Saving card...' : 'Save card'}
      </Button>

      <p className="text-xs text-center text-body">
        Your card details go straight to Stripe. FASH never sees your full card number.
      </p>
    </form>
  );
}

export function AddPaymentMethodSheet({ isOpen, onClose, onAdded }: AddPaymentMethodSheetProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable<Record<string, never>, SetupIntentResponse>(
        functions,
        'createSetupIntent',
      );
      const { data } = await call({});
      setStripePromise(getStripe(data.publishableKey));
      setClientSecret(data.clientSecret);
    } catch (err: any) {
      setError(err?.message || 'Could not start card setup. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // A SetupIntent is single use, so each opening of the sheet gets a fresh one.
  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setStripePromise(null);
      setError(null);
      return;
    }
    void start();
  }, [isOpen, start]);

  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-label="Close add payment method"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add a payment method"
        className="relative w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl shadow-xl max-h-[88vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3.5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-heading">Add a payment method</h2>
            <p className="text-xs text-body">Saved for faster checkout next time</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-heading" />
          </button>
        </div>

        <div className="px-4 py-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-body">Setting up a secure form...</p>
            </div>
          )}

          {!loading && error && (
            <div className="py-6 space-y-4">
              <div className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5">
                <AlertCircle className="h-4 w-4 text-error mt-0.5 flex-shrink-0" />
                <p className="text-xs text-error">{error}</p>
              </div>
              <Button variant="outline" fullWidth onClick={() => void start()}>
                Try again
              </Button>
            </div>
          )}

          {!loading && !error && clientSecret && stripePromise && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
            >
              <SetupForm onAdded={onAdded} onClose={onClose} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}
